import { trackConfig, type IFramework } from '@/framework';
import {
    LayerType,
    type ILayerProps,
    type IVectorLayerProps,
    type TextAlignType,
    type TextVerticalAlign,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, resolveVectorTextLayout, updateLayer } from '../utils';

export interface ITextAlignment {
    /**
     * 设置水平对齐方式
     * @param layerID - 图层 id
     * @param align - 水平对齐方式
     * @returns
     */
    setTextAlign(layerID: string, value: TextAlignType): ILayerProps | void;

    /**
     * 设置垂直对齐方式
     * @param layerID - 图层 id
     * @param align - 垂直对齐方式
     * @returns
     */
    setVerticalAlign(layerID: string, value: TextVerticalAlign): ILayerProps | void;
}

export class TextAlignment implements ITextAlignment {
    constructor(private framework: IFramework) {}

    private set(layerID: string, kind: 'textAlign', value: TextAlignType): ILayerProps | void;
    private set(layerID: string, kind: 'verticalAlign', value: TextVerticalAlign): ILayerProps | void;
    /**
     * 更新文字对齐；水平对齐重新排版，交叉轴对齐只更新组合定位。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param kind - 水平或交叉轴对齐字段。
     * @param value - 新对齐值。
     * @returns 更新后的 Vector；输入不适用或值未变化时返回 void。
     */
    private set(layerID: string, kind: 'textAlign' | 'verticalAlign', value: TextAlignType | TextVerticalAlign) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        if (textInfo[kind] === value) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: value };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const candidate = { ...prevLayer, data: nextLayerData };

        // verticalAlign 不进入 TextRenderInput，只决定已排版 layoutBox 在 Vector 内容框中的位置；
        // 跳过 layout seam 也避免对齐操作意外改变图层逻辑几何。
        const nextLayer = kind === 'verticalAlign' ? candidate : resolveVectorTextLayout(this.framework, candidate);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].textAlign);

        return nextLayer;
    }

    setTextAlign(layerID: string, value: TextAlignType) {
        return this.set(layerID, 'textAlign', value);
    }

    setVerticalAlign(layerID: string, value: TextVerticalAlign) {
        return this.set(layerID, 'verticalAlign', value);
    }
}
